import { useEffect, useState } from "react";
import { ShieldAlert, TriangleAlert } from "lucide-react";
import EventsOverTimeChart from "../components/EventsOverTimeChart";
import {
  api,
  type Overview as OverviewData,
  type CustomersData,
  type EventTypeBreakdown,
  type FailuresData,
  type FailureRow,
} from "../lib/api";
import { EmptyState } from "@/components/empty-state";
import {
  AdvancedSection,
  ErrorState,
  FormSection,
  StatTile,
} from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { RangeSelect, useRangeParam } from "@/components/range-select";
import { StatusBadge } from "@/components/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function failureSummary(f: FailureRow): string {
  if (f.event === "safety_gate_failed") {
    if (f.reason && f.reason.trim().length > 0) return f.reason;
    if (f.checks) {
      const failed = Object.entries(f.checks)
        .filter(([, v]) => !v.pass)
        .map(([k, v]) => (v.reason ? `${k}: ${v.reason}` : k));
      if (failed.length > 0) return failed.join("; ");
    }
    return "Safety-Gate abgelehnt (ohne Grund)";
  }
  return f.message || f.reason || "unbekannter Fehler";
}

const num = (v: number | string | undefined) =>
  v === undefined ? "—" : Number(v).toLocaleString("de-DE");

/**
 * Übersicht über Shop-Personalisierung: enthält auch die frühere Seite
 * „Customers“ (neue/wiederkehrende Sessions, Safety-Gate, Event-Typen).
 */
export default function Overview() {
  const [days, setDays] = useRangeParam(30);
  const [data, setData] = useState<OverviewData | null>(null);
  const [customers, setCustomers] = useState<CustomersData | null>(null);
  const [breakdown, setBreakdown] = useState<EventTypeBreakdown | null>(null);
  const [failures, setFailures] = useState<FailuresData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setData(null);
    setCustomers(null);
    setBreakdown(null);
    setFailures(null);
    setError(null);
    const fail = (e: unknown) => setError((e as Error).message);
    api.overview(days).then(setData).catch(fail);
    api.customers(days).then(setCustomers).catch(fail);
    api.eventTypes(days).then(setBreakdown).catch(fail);
    api.recentFailures(days, 25).then(setFailures).catch(fail);
  }, [days, reloadKey]);

  // Quote aus Ergebnissen statt aus Anfragen: preview_requested schickt das
  // Theme nicht mehr zuverlässig, dadurch lag die alte Quote über 100 %.
  const finished = data
    ? Number(data.previews_generated) + Number(data.previews_failed)
    : 0;
  const successRate =
    data && finished > 0
      ? `${((Number(data.previews_generated) / finished) * 100).toFixed(1).replace(".", ",")} % erfolgreich`
      : undefined;

  return (
    <div>
      <PageHeader
        title="Übersicht"
        actions={<RangeSelect value={days} onChange={setDays} />}
      />

      {error && (
        <ErrorState
          className="mb-4"
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}

      <div className="space-y-4">
        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Verkauf</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Umsatz"
              loading={!data}
              value={
                data &&
                Number(data.revenue).toLocaleString("de-DE", {
                  style: "currency",
                  currency: "EUR",
                })
              }
            />
            <StatTile
              label="Käufe"
              loading={!data}
              value={num(data?.purchases)}
            />
            <StatTile
              label="In den Warenkorb"
              loading={!data}
              value={num(data?.add_to_cart)}
            />
            <StatTile
              label="Sessions"
              loading={!data || !customers}
              value={num(data?.unique_sessions)}
              hint={
                customers &&
                `${num(customers.new_sessions)} neu · ${num(customers.returning_sessions)} wiederkehrend`
              }
            />
          </div>
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Previews</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Previews generiert"
              loading={!data}
              value={num(data?.previews_generated)}
              hint={successRate}
            />
            <StatTile
              label="Previews fehlgeschlagen"
              loading={!data}
              value={num(data?.previews_failed)}
            />
            <StatTile
              label="Safety-Gate abgelehnt"
              loading={!customers}
              value={num(customers?.safety_gate_failed)}
            />
            <StatTile
              label="Events gesamt"
              loading={!data}
              value={num(data?.total_events)}
            />
          </div>
        </section>

        <EventsOverTimeChart
          days={days}
          interval={days <= 2 ? "hour" : "day"}
          events={[
            "personalization_started",
            "preview_generated",
            "character_rated",
            "add_to_cart",
            "purchase",
          ]}
        />

        <FormSection
          title="Letzte Fehler"
          description="Fehlgeschlagene Previews und Safety-Gate-Ablehnungen im Zeitraum."
          contentClassName="px-0"
        >
          {!failures ? (
            <div className="space-y-2 px-4">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : failures.failures.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="Keine Fehler"
              description="Im Zeitraum sind keine Previews fehlgeschlagen oder abgelehnt worden."
            />
          ) : (
            <ul className="divide-y">
              {failures.failures.map((f, i) => (
                <li key={i} className="space-y-1 px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    {f.event === "safety_gate_failed" ? (
                      <StatusBadge tone="warning">Safety-Gate</StatusBadge>
                    ) : (
                      <StatusBadge tone="critical">Preview-Fehler</StatusBadge>
                    )}
                    {f.kind && (
                      <StatusBadge tone="neutral">{f.kind}</StatusBadge>
                    )}
                    {f.attempt && (
                      <span className="text-xs text-muted-foreground">
                        Versuch {f.attempt}
                      </span>
                    )}
                    <time className="ml-auto text-xs text-muted-foreground">
                      {new Date(f.ts).toLocaleString("de-DE", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </time>
                  </div>
                  <p className="break-words">{failureSummary(f)}</p>
                  {f.uniqid && (
                    <p className="font-mono text-xs text-muted-foreground">
                      {f.uniqid}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </FormSection>

        <FormSection title="Alle Event-Typen" size="sm">
          <AdvancedSection
            label={
              breakdown
                ? `${breakdown.events.length} Event-Typen anzeigen`
                : "Event-Typen anzeigen"
            }
          >
            {!breakdown ? (
              <Skeleton className="h-24" />
            ) : breakdown.events.length === 0 ? (
              <p className="flex items-center gap-1.5 text-muted-foreground">
                <TriangleAlert className="size-4" /> Keine Events im Zeitraum.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Event</TableHead>
                    <TableHead className="text-right">Anzahl</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdown.events.map((row) => (
                    <TableRow key={row.event}>
                      <TableCell className="font-mono text-xs">
                        {row.event}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {num(row.count)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </AdvancedSection>
        </FormSection>
      </div>
    </div>
  );
}
